import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Avatar from "@/components/Avatar";
import YearInTheLord from "@/components/YearInTheLord";
import KingdomSupporterBadge from "@/components/KingdomSupporterBadge";
import {
  ArrowLeft, Calendar as CalIcon, Image as ImageIcon, MessageSquare,
  Quote, Trophy, Users, Send,
} from "lucide-react";
import { toast } from "sonner";

const TIER_CLASS = {
  Seeker: "bg-cream-surface text-jjblue",
  Believer: "bg-gold/15 text-burgundy",
  Disciple: "bg-burgundy text-cream",
};

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export default function MemberProfile() {
  const { userId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/users/${userId}`)
      .then(({ data }) => { if (alive) setData(data); })
      .catch((e) => {
        toast.error(e?.response?.data?.detail || "Member not found");
        navigate("/feed");
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [userId, navigate]);

  if (loading) return <div className="max-w-5xl mx-auto px-6 py-20 text-center text-[var(--jj-muted)]">Loading…</div>;
  if (!data) return null;

  const tierClass = TIER_CLASS[data.tier] || TIER_CLASS.Seeker;
  const initial = (data.name || "·")[0].toUpperCase();

  return (
    <div className="max-w-5xl mx-auto px-6 sm:px-8 py-10">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm text-jjblue/80 hover:text-burgundy mb-6" data-testid="member-back">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      {/* Hero */}
      <div className="jj-card p-7 sm:p-9 relative overflow-hidden" data-testid="member-card">
        <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-r from-jjblue via-burgundy to-jjblue opacity-90" />
        <div className="relative flex flex-col sm:flex-row sm:items-end gap-5">
          <div className="border-4 border-cream shadow-lg rounded-full">
            <Avatar user={data} size={96} testId="member-avatar" />
          </div>
          <div className="flex-1 sm:pb-2">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-serif text-3xl sm:text-4xl text-jjblue leading-tight" data-testid="member-name">{data.name}</h1>
              {data.awarded_at && (
                <span title="Honored as a top inviter" className="inline-flex items-center gap-1 bg-gold/15 text-burgundy text-[10px] font-semibold tracking-wider uppercase px-2 py-1 rounded-full">
                  <Trophy className="h-3 w-3" /> Honored
                </span>
              )}
              {data.kingdom_supporter && (
                <KingdomSupporterBadge since={data.supporter_since} />
              )}
            </div>
            <div className="mt-2 flex items-center gap-2 text-xs text-[var(--jj-muted)] flex-wrap">
              <span className={`px-2 py-0.5 rounded-full ${tierClass}`}>{data.tier}</span>
              {data.profile_type && <span className="capitalize">· {String(data.profile_type).replace(/_/g, " ")}</span>}
              {data.joined_at && <span>· Joined {fmtDate(data.joined_at)}</span>}
              {data.invite_signups > 0 && (
                <span className="inline-flex items-center gap-1">· <Users className="h-3 w-3" /> brought in {data.invite_signups}</span>
              )}
            </div>
            {data.bio && (
              <p className="mt-3 text-jjblue/85 leading-relaxed max-w-2xl whitespace-pre-wrap" data-testid="member-bio">{data.bio}</p>
            )}
          </div>
          <div className="sm:pb-2">
            {data.is_self ? (
              <Link to="/profile" className="btn-secondary !py-2 !px-4 text-sm" data-testid="member-self-link">
                That&rsquo;s you · edit
              </Link>
            ) : (
              <Link to={`/messages/${data.id}`} className="btn-primary !py-2.5 !px-5 text-sm inline-flex items-center gap-2" data-testid="member-message">
                <Send className="h-4 w-4" /> Send a message
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Counts */}
      <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Posts", value: data.counts.posts, Icon: MessageSquare },
          { label: "Testimonies", value: data.counts.testimonies, Icon: Quote },
          { label: "Memories", value: data.counts.memories, Icon: ImageIcon },
          { label: "Prayers", value: data.counts.prayers, Icon: CalIcon },
        ].map(({ label, value, Icon }) => (
          <div key={label} className="bg-cream-surface rounded-xl p-4 flex items-center gap-3">
            <Icon className="h-5 w-5 text-burgundy" strokeWidth={1.5} />
            <div>
              <div className="font-serif text-xl text-jjblue leading-none">{value}</div>
              <div className="text-[10px] uppercase tracking-wider text-[var(--jj-muted)] mt-1">{label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Their Year in the Lord */}
      <YearInTheLord userId={data.id} />

      {/* Recent testimonies */}
      {data.recent_testimonies?.length > 0 && (
        <section className="mt-10">
          <h2 className="font-serif text-2xl text-jjblue mb-3">Recent testimonies</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {data.recent_testimonies.map((t) => (
              <article key={t.id} className="jj-card p-5" data-testid={`member-testimony-${t.id}`}>
                <Quote className="h-4 w-4 text-burgundy" strokeWidth={1.4} />
                {t.title && <h3 className="font-serif text-lg text-jjblue mt-2 leading-tight">{t.title}</h3>}
                <p className="mt-2 text-sm text-[var(--jj-text-secondary)] whitespace-pre-wrap line-clamp-5">{t.content}</p>
                <div className="mt-3 text-[10px] uppercase tracking-wider text-[var(--jj-muted)]">{fmtDate(t.created_at)}</div>
              </article>
            ))}
          </div>
        </section>
      )}

      {/* Recent posts */}
      {data.recent_posts?.length > 0 && (
        <section className="mt-10">
          <h2 className="font-serif text-2xl text-jjblue mb-3">Recent shares</h2>
          <ul className="space-y-2">
            {data.recent_posts.map((p) => (
              <li key={p.id} className="jj-card p-4 flex items-start gap-3" data-testid={`member-post-${p.id}`}>
                <MessageSquare className="h-4 w-4 text-burgundy mt-1 shrink-0" strokeWidth={1.4} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-jjblue whitespace-pre-wrap line-clamp-3">{p.content}</p>
                  <div className="mt-2 flex items-center gap-3 text-[11px] text-[var(--jj-muted)]">
                    <span>{fmtDate(p.created_at)}</span>
                    {p.comments_count > 0 && <span>· {p.comments_count} comment{p.comments_count === 1 ? "" : "s"}</span>}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Recent memories */}
      {data.recent_memories?.length > 0 && (
        <section className="mt-10">
          <h2 className="font-serif text-2xl text-jjblue mb-3">Memory wall</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {data.recent_memories.map((m) => (
              <Link
                to="/memories"
                key={m.id}
                className="block aspect-square rounded-xl overflow-hidden bg-cream-surface border border-jj"
                data-testid={`member-memory-${m.id}`}
              >
                {m.media_kind === "video" ? (
                  <video src={fileUrl(m.media_path)} className="w-full h-full object-cover" muted />
                ) : (
                  <img src={fileUrl(m.media_path)} alt={m.caption || ""} loading="lazy" className="w-full h-full object-cover" />
                )}
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Empty state */}
      {data.recent_testimonies?.length === 0 && data.recent_posts?.length === 0 && data.recent_memories?.length === 0 && (
        <div className="mt-10 text-center text-sm text-[var(--jj-muted)] py-10">
          {data.name} hasn&rsquo;t shared anything yet. Be the first to send them a word of encouragement.
        </div>
      )}
    </div>
  );
}
